(() => {
  const READY_KEY = 'lumppingsCartNotificationReady';
  const VISIBLE_CLASSES = ['translate-y-0', 'opacity-100'];
  const HIDDEN_CLASSES = ['translate-y-2', 'opacity-0'];
  const CART_UPDATE_SOURCES = new Set(['product-form-component', 'lumppings-product-hero']);

  const escapeHTML = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));

  const themeRoutes = () => {
    if (typeof Theme !== 'undefined' && Theme.routes) return Theme.routes;
    return window.Theme?.routes;
  };

  const itemImage = (item) => item.featured_image?.url || item.image || '';
  const mobileQuery = window.matchMedia('(max-width: 639px)');

  const cartJsonUrl = (notification) => {
    const cartUrl = themeRoutes()?.cart_url || notification.dataset.cartUrl;
    if (!cartUrl) return null;
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  };

  const formatMoney = (notification, cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: notification.dataset.currency || 'USD',
    }).format(value);
  };

  const renderEmpty = (notification) => (
    `<p class="rounded-xl bg-[#F4F4FA] px-3 py-3 text-sm text-[#6B6B6B]">${escapeHTML(notification.dataset.emptyText)}</p>`
  );

  const renderItem = (notification, item) => {
    const image = itemImage(item);
    const title = item.product_title || item.title || '';
    const safeTitle = escapeHTML(title);
    const quantity = Number(item.quantity || 1);
    const price = formatMoney(notification, item.final_line_price ?? item.line_price ?? item.price);

    return `
      <div class="flex min-w-0 items-center gap-3">
        <div class="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-[#F4F4FA]">
          ${image ? `<img src="${escapeHTML(image)}" alt="${safeTitle}" class="absolute inset-0 h-full w-full object-cover" loading="lazy">` : ''}
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium leading-snug text-[#0A0A0A]">${safeTitle}</p>
          <p class="mt-1 whitespace-nowrap text-xs text-[#6B6B6B]">Qty ${quantity}</p>
        </div>
        <p class="shrink-0 whitespace-nowrap text-sm font-medium text-[#0A0A0A]">${price}</p>
      </div>
    `;
  };

  const fetchCart = async (notification) => {
    const url = cartJsonUrl(notification);
    if (!url) return null;
    const response = await fetch(url);
    return response.ok ? response.json() : null;
  };
  const renderCart = (notification, cart) => {
    if (!cart) return;

    const itemsRoot = notification.querySelector('[data-lumppings-cart-notification-items]');
    const summary = notification.querySelector('[data-lumppings-cart-notification-summary]');
    const count = Number(cart.item_count || 0);

    if (summary) {
      summary.textContent = `${notification.dataset.countPrefix || 'Cart items:'} ${count}`;
    }

    if (!itemsRoot) return;
    itemsRoot.innerHTML = count ? cart.items.slice(0, 4).map((item) => renderItem(notification, item)).join('') : renderEmpty(notification);
  };

  const initNotification = (notification) => {
    if (notification.dataset[READY_KEY] === 'true') return;
    notification.dataset[READY_KEY] = 'true';

    const root = notification.parentElement;
    const trigger = root?.querySelector('[data-lumppings-cart-trigger]');
    const close = notification.querySelector('[data-lumppings-cart-notification-close]');
    let timeout;
    let hoverOpen = false;
    let cartRequest;

    const isHoverTarget = (node) => (
      node instanceof Node && Boolean(trigger?.contains(node) || notification.contains(node))
    );

    const hide = () => {
      hoverOpen = false;
      window.clearTimeout(timeout);
      notification.classList.add(...HIDDEN_CLASSES);
      notification.classList.remove(...VISIBLE_CLASSES);
      timeout = window.setTimeout(() => notification.classList.add('hidden'), 220);
    };

    const show = (cart, options = {}) => {
      renderCart(notification, cart);
      window.clearTimeout(timeout);
      notification.classList.remove('hidden');
      requestAnimationFrame(() => {
        notification.classList.remove(...HIDDEN_CLASSES);
        notification.classList.add(...VISIBLE_CLASSES);
      });
      if (!options.persist) timeout = window.setTimeout(hide, 4200);
    };

    const showCurrentCart = async () => {
      if (mobileQuery.matches) return;
      hoverOpen = true;
      cartRequest = cartRequest || fetchCart(notification).finally(() => {
        cartRequest = null;
      });
      const cart = await cartRequest;
      if (hoverOpen) show(cart, { persist: true });
    };

    const scheduleHoverHide = (event) => {
      if (isHoverTarget(event?.relatedTarget)) return;
      hoverOpen = false;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        if (!hoverOpen) hide();
      }, 180);
    };

    const isTriggerEvent = (event) => {
      const target = event.target;
      return target instanceof Element && target.closest('[data-lumppings-cart-trigger]') === trigger;
    };

    document.addEventListener('cart:update', async (event) => {
      if (event.detail?.data?.didError) return;

      const source = event.detail?.data?.source;
      if (source && !CART_UPDATE_SOURCES.has(source)) return;

      const cart = event.detail?.resource?.item_count === undefined ? await fetchCart(notification) : event.detail.resource;
      show(cart);
    });

    trigger?.addEventListener('mouseenter', showCurrentCart);
    trigger?.addEventListener('focus', showCurrentCart);
    trigger?.addEventListener('mouseleave', scheduleHoverHide);
    trigger?.addEventListener('blur', scheduleHoverHide);

    document.addEventListener('mouseover', (event) => {
      if (isTriggerEvent(event)) showCurrentCart();
    });
    document.addEventListener('focusin', (event) => {
      if (isTriggerEvent(event)) showCurrentCart();
    });
    document.addEventListener('mouseout', (event) => {
      if (isTriggerEvent(event)) scheduleHoverHide(event);
    });
    document.addEventListener('focusout', (event) => {
      if (isTriggerEvent(event)) scheduleHoverHide(event);
    });

    notification.addEventListener('mouseenter', () => {
      hoverOpen = true;
      window.clearTimeout(timeout);
    });
    notification.addEventListener('mouseleave', scheduleHoverHide);
    close?.addEventListener('click', hide);
  };

  const initAllNotifications = () => {
    document.querySelectorAll('[data-lumppings-cart-notification]').forEach(initNotification);
  };

  initAllNotifications();
  document.addEventListener('shopify:section:load', initAllNotifications);
})();
